import React from 'react';
import { FaTimes, FaBan, FaEnvelope, FaSpinner } from 'react-icons/fa';

// Styled rejection modal. Replaces the native window.prompt() that used to
// capture rejection notes. Same double-click protection as ApprovalModal:
// loading overlay swallows all clicks while the round-trip is in flight,
// and the parent hook holds a useRef synchronous lock.
const RejectModal = ({
    customer,
    onClose,
    onReject,
    rejectionLoading,
    rejectionNotes,
    setRejectionNotes,
    sendEmail,
    setSendEmail,
}) => {
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-[#282828] rounded-xl max-w-2xl w-full max-h-[90vh] flex flex-col relative">
                {/* Full-modal lock overlay during rejection round-trip (same
                    pattern as ApprovalModal). Belt-and-braces against double
                    submit even though Reject doesn't charge Stripe. */}
                {rejectionLoading && (
                    <div
                        className="absolute inset-0 bg-[#1d2021] bg-opacity-80 rounded-xl z-10 flex items-center justify-center cursor-wait"
                        onClickCapture={(e) => { e.stopPropagation(); e.preventDefault(); }}
                    >
                        <div className="flex flex-col items-center gap-3 text-[#ebdbb2]">
                            <FaSpinner className="animate-spin text-[#fb4934]" size={32} />
                            <p className="text-lg font-semibold">Rejecting customer…</p>
                            <p className="text-sm text-[#928374]">Sending rejection email & updating record</p>
                        </div>
                    </div>
                )}

                <div className="p-6 border-b border-[#3c3836] flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <h2 className="text-2xl font-bold text-[#fb4934] flex items-center gap-2">
                            <FaBan /> Reject Customer
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-[#928374] hover:text-[#bdae93]"
                            disabled={rejectionLoading}
                        >
                            <FaTimes size={24} />
                        </button>
                    </div>
                </div>

                <div className="p-6 overflow-y-auto flex-1">
                    {/* Customer Info */}
                    <div className="mb-6 p-4 bg-[#3c3836] rounded-lg">
                        <h3 className="text-lg font-semibold text-[#ebdbb2] mb-3">Customer</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div>
                                <span className="font-medium text-[#bdae93]">Name:</span>
                                <p className="text-[#ebdbb2]">{customer?.fullName}</p>
                            </div>
                            <div>
                                <span className="font-medium text-[#bdae93]">Email:</span>
                                <p className="text-[#ebdbb2]">{customer?.email}</p>
                            </div>
                            <div>
                                <span className="font-medium text-[#bdae93]">Plan:</span>
                                <p className="text-[#ebdbb2]">{customer?.plan}</p>
                            </div>
                            <div>
                                <span className="font-medium text-[#bdae93]">Status:</span>
                                <p className="text-[#fabd2f] uppercase font-mono text-xs">{customer?.status}</p>
                            </div>
                        </div>
                    </div>

                    {/* What will happen */}
                    <div className="mb-6 p-4 bg-[#3c1f1f] border border-[#cc241d] rounded-lg">
                        <h3 className="text-sm font-semibold text-[#fb4934] mb-2">What rejecting does</h3>
                        <ul className="text-sm text-[#fbf1c7] space-y-1 list-disc list-inside">
                            <li>Sets customer status to <code className="bg-[#1d2021] px-1 rounded text-[#fabd2f]">rejected</code></li>
                            <li>Records the rejection notes + timestamp on the customer record</li>
                            <li>Sends a rejection email (unless unchecked below)</li>
                            <li>Logs the action to the customer's action history</li>
                        </ul>
                        <p className="text-xs text-[#928374] mt-3">
                            Note: rejection does <em>not</em> delete the Cognito user. The customer can't re-signup with
                            the same email after rejection — if you want them to be able to retry, use Cancel Service flow
                            instead (it frees the email).
                        </p>
                    </div>

                    {/* Rejection Notes */}
                    <div className="mb-6">
                        <label htmlFor="rejectionNotes" className="block text-sm font-medium text-[#bdae93] mb-2">
                            Rejection Reason / Notes <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            id="rejectionNotes"
                            value={rejectionNotes}
                            onChange={(e) => setRejectionNotes(e.target.value)}
                            placeholder="e.g. ID document blurry — could not verify identity; please re-upload and re-sign."
                            className="w-full px-3 py-2 bg-[#1d2021] border border-[#504945] rounded-lg text-[#ebdbb2] focus:ring-2 focus:ring-[#fb4934] focus:border-transparent"
                            rows={4}
                            disabled={rejectionLoading}
                        />
                        <p className="text-xs text-[#928374] mt-1">
                            These notes are recorded permanently and included in the rejection email.
                        </p>
                    </div>

                    {/* Send Email Checkbox */}
                    <div className="mb-6 p-3 bg-[#3c3836] rounded-lg">
                        <label className="flex items-center gap-3 cursor-pointer text-sm">
                            <input
                                type="checkbox"
                                checked={sendEmail}
                                onChange={(e) => setSendEmail(e.target.checked)}
                                disabled={rejectionLoading}
                                className="w-4 h-4 accent-[#fb4934]"
                            />
                            <FaEnvelope className="text-[#83a598]" />
                            <span className="text-[#ebdbb2]">
                                Send rejection email to <code className="text-[#fabd2f]">{customer?.email}</code>
                            </span>
                        </label>
                        <p className="text-xs text-[#928374] mt-1 ml-7">
                            Uncheck only for internal corrections — most rejections should notify the customer.
                        </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-end space-x-3 pt-4 border-t border-[#3c3836]">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 border border-[#504945] text-[#bdae93] rounded-lg hover:bg-[#3c3836]"
                            disabled={rejectionLoading}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onReject}
                            disabled={rejectionLoading || !rejectionNotes.trim()}
                            title={!rejectionNotes.trim() ? 'Rejection notes are required' : ''}
                            className="px-6 py-2 bg-[#fb4934] text-[#1d2021] rounded-lg hover:bg-[#cc241d] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 font-medium"
                        >
                            {rejectionLoading ? (
                                <>
                                    <FaSpinner className="animate-spin" />
                                    <span>Rejecting...</span>
                                </>
                            ) : (
                                <>
                                    <FaBan />
                                    <span>Reject Customer</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RejectModal;
